import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useMemo } from 'react';
import { useBreadcrumbJsonLd } from '../hooks/usePageMeta';

const BASE_URL = 'https://innovationcoders.com';

const labelMap: Record<string, string> = {
  services: 'Services',
  portfolio: 'Portfolio',
  'case-studies': 'Case Studies',
  pricing: 'Pricing',
  blog: 'Blog',
  about: 'About',
  contact: 'Contact',
};

function formatSegment(segment: string): string {
  if (labelMap[segment]) return labelMap[segment];
  // Convert slug to title case
  return segment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function Breadcrumbs() {
  const location = useLocation();

  const segments = location.pathname.split('/').filter(Boolean);

  const crumbs = segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    const label = formatSegment(segment);
    const isLast = index === segments.length - 1;
    return { path, label, isLast };
  });

  // Build breadcrumb items for JSON-LD
  const breadcrumbItems = useMemo(() => {
    const items = [{ name: 'Home', url: `${BASE_URL}/` }];
    for (const crumb of crumbs) {
      items.push({ name: crumb.label, url: `${BASE_URL}${crumb.path}` });
    }
    return items;
  }, [crumbs]);

  // Inject BreadcrumbList JSON-LD
  useBreadcrumbJsonLd(location.pathname === '/' ? [] : breadcrumbItems);

  // Don't show on homepage
  if (location.pathname === '/') return null;

  return (
    <nav aria-label="Breadcrumb" className="border-b bg-muted/30">
      <div className="container mx-auto px-4">
        <ol className="flex items-center gap-1.5 py-3 text-sm text-muted-foreground overflow-x-auto">
          <li className="flex items-center gap-1.5">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-primary transition-colors whitespace-nowrap"
            >
              <Home className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Home</span>
            </Link>
          </li>
          {crumbs.map((crumb) => (
            <li key={crumb.path} className="flex items-center gap-1.5">
              <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {crumb.isLast ? (
                <span className="font-medium text-foreground whitespace-nowrap" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="hover:text-primary transition-colors whitespace-nowrap"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
